import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Settings2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import CalcInfo from "@/components/drill/CalcInfo";
import TimeScroller from "@/components/ui/time-scroller";

const nowTime = () => new Date().toTimeString().slice(0, 5);

export default function ChipSectionForm({
  onSubmit,
  onAddRod,
  initialData,
  defaultFromDepth = 0,
  rodLength = 6,
  bhaLength = 0,
  tableHeight = 0,
  onBhaChange,
  defaultRodNumber = 1,
  finishIncompleteRod = false,
}) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [newBha, setNewBha] = useState("");
  const [finishingIncomplete, setFinishingIncomplete] = useState(!!finishIncompleteRod);
  const [rodCountAdjustment, setRodCountAdjustment] = useState(0);

  const resetForNextRod = (fromDepth) => {
    setForm({
      from_depth: +Number(fromDepth || 0).toFixed(2),
      rod_length: rodLength,
      bha_length: Number(form.bha_length || bhaLength || 0),
      rod_count: 0,
      rod_number: defaultRodNumber,
      start_time: nowTime(),
      end_time: "",
      rod_not_completed: false,
      stick_up: 0,
      lithology: "",
      remarks: "",
    });
  };

  useEffect(() => {
    setFinishingIncomplete(!!finishIncompleteRod);
    setRodCountAdjustment(0);
    setForm(
      initialData
        ? {
            ...initialData,
            rod_length: initialData.rod_length ?? rodLength,
            bha_length: initialData.bha_length ?? bhaLength,
          }
        : {
            from_depth: defaultFromDepth,
            rod_length: rodLength,
            bha_length: bhaLength,
            rod_count: 0,
            rod_number: defaultRodNumber,
            start_time: nowTime(),
            end_time: "",
            rod_not_completed: false,
            stick_up: 0,
            lithology: "",
            remarks: "",
          }
    );
  }, [initialData, defaultFromDepth, rodLength, bhaLength, defaultRodNumber, finishIncompleteRod]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Chip Total Depth is calculated from the absolute rod position:
  // (number of rods × rod length) + BHA − table height − stick up.
  // Stick Up only reduces Total Depth when the current rod is incomplete.
  const calculatedDepth = useMemo(() => {
    const rods = Number(form.rod_number) + Number(rodCountAdjustment || 0);
    const length = Number(form.rod_length || rodLength);
    const bha = Number(form.bha_length ?? bhaLength ?? 0);
    const table = Number(tableHeight || 0);
    const stickUp = form.rod_not_completed ? Number(form.end_stick_up ?? 0) : 0;

    if (![rods, length, bha, table, stickUp].every(Number.isFinite)) return null;
    return +(rods * length + bha - table - stickUp).toFixed(2);
  }, [form.rod_number, form.rod_length, form.bha_length, form.rod_not_completed, form.end_stick_up, rodLength, bhaLength, tableHeight, rodCountAdjustment]);

  const completedDepth = calculatedDepth;

  const baseRodsInHole = useMemo(() => {
    if (calculatedDepth == null) return null;
    const divisor = Number(form.rod_length || rodLength);
    if (!divisor) return null;
    const value = (calculatedDepth - Number(form.bha_length || 0) - Number(tableHeight || 0)) / divisor;
    return Number.isFinite(value) ? Math.max(0, Math.ceil(value)) : null;
  }, [calculatedDepth, form.bha_length, form.rod_length, rodLength, tableHeight]);

  // Stick Up must not directly change Rods In Hole. Rods In Hole is driven
  // only by the normal depth/BHA/table-height/rod-length calculation.
  const rodsInHole = baseRodsInHole;

  const addRod = async () => {
    if (!onAddRod || calculatedDepth == null) return;
    setSubmitting(true);
    try {
      const data = {
        record_type: "chip_section",
        ...form,
        rod_number: Number(form.rod_number ?? defaultRodNumber),
        rod_not_completed: finishingIncomplete ? false : !!form.rod_not_completed,
        stick_up: finishingIncomplete ? 0 : Number(form.stick_up ?? 0),
        from_depth: Number(form.from_depth || 0),
        to_depth: finishingIncomplete ? calculatedDepth : completedDepth,
        rod_length: Number(form.rod_length || rodLength),
        rods_in_hole: rodsInHole,
        start_time: form.start_time || nowTime(),
        end_time: form.end_time || null,
        end_stick_up: finishingIncomplete ? null : (form.end_stick_up ?? null),
      };
      const created = await onAddRod(data);
      if (created !== false) {
        const nextDepth = created?.to_depth ?? completedDepth ?? calculatedDepth;
        const currentWasIncomplete = !!form.rod_not_completed;
        resetForNextRod(nextDepth);
        setFinishingIncomplete(currentWasIncomplete);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const changeBha = async () => {
    const oldBha = Number(form.bha_length || 0);
    const value = Number(newBha);
    if (!Number.isFinite(value)) return;
    const delta = value - oldBha;

    // A completed rod always has zero Stick Up. Only an incomplete rod carries
    // an entered Stick Up, which must move by the BHA delta.
    const stickUp = form.rod_not_completed
      ? +(Number(form.stick_up || 0) + delta).toFixed(2)
      : 0;

    // A single Chip drilling instance cannot exceed 6 m from its From Depth.
    // If the BHA change would make the current instance deeper than 6 m,
    // remove one rod from the current rod count and recalculate Total Depth.
    const currentRodCount = Math.max(0, Math.floor(Number(form.rod_number ?? defaultRodNumber) || 0) + Number(rodCountAdjustment || 0));
    const length = Number(form.rod_length || rodLength);
    const table = Number(tableHeight || 0);
    const from = Number(form.from_depth || 0);
    const proposedDepth = currentRodCount * length + value - table - stickUp;
    const exceedsSixMetres = Number.isFinite(proposedDepth) && Number.isFinite(from) && proposedDepth - from > 6;
    const adjustedRodCount = exceedsSixMetres ? Math.max(0, currentRodCount - 1) : currentRodCount;
    const nextAdjustment = adjustedRodCount - Math.max(0, Math.floor(Number(form.rod_number ?? defaultRodNumber) || 0));

    setRodCountAdjustment(nextAdjustment);
    setForm((p) => ({
      ...p,
      bha_length: value,
      stick_up: stickUp,
    }));
    try { await onBhaChange?.(value); } catch (e) { console.error(e); }
    setNewBha("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        record_type: "chip_section",
        ...form,
        rod_number: Number(form.rod_number ?? defaultRodNumber),
        rod_not_completed: finishingIncomplete ? false : !!form.rod_not_completed,
        stick_up: finishingIncomplete ? 0 : Number(form.stick_up ?? 0),
        to_depth: finishingIncomplete ? calculatedDepth : (completedDepth ?? form.to_depth),
        rods_in_hole: rodsInHole,
        end_stick_up: finishingIncomplete ? null : form.end_stick_up,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>From Depth (m)</Label>
          <Input readOnly value={form.from_depth ?? ""} className="rounded-xl bg-slate-50 dark:bg-slate-800" />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <Label>Total Depth (m)</Label>
            <CalcInfo title="Total depth">(Number of rods × Rod Length) + BHA − Table Height − Stick Up. Stick Up is subtracted only when the rod is not completed.</CalcInfo>
          </div>
          <Input readOnly value={completedDepth ?? ""} className="rounded-xl bg-slate-50 dark:bg-slate-800" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Rod Length (m)</Label>
          <Input readOnly value={form.rod_length ?? rodLength} className="rounded-xl bg-slate-50 dark:bg-slate-800" />
        </div>
        <div>
          <Label>BHA (m)</Label>
          <Input readOnly value={form.bha_length ?? bhaLength} className="rounded-xl bg-slate-50 dark:bg-slate-800" />
        </div>
        <div>
          <Label>Rods in Hole</Label>
          <Input readOnly value={rodsInHole ?? ""} className="rounded-xl bg-slate-50 dark:bg-slate-800" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={addRod} disabled={submitting} className="rounded-xl gap-2">
          <Plus className="w-4 h-4" /> {finishingIncomplete ? "Finish Incomplete Rod" : "Add Rod"}
        </Button>
        <div className="flex gap-2 items-center">
          <Input value={newBha} onChange={(e) => setNewBha(e.target.value)} type="number" step="0.01" placeholder="New BHA" className="rounded-xl w-32" />
          <Button type="button" variant="outline" onClick={changeBha} className="rounded-xl gap-2">
            <Settings2 className="w-4 h-4" /> Change BHA
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div><Label>Start Time</Label><TimeScroller value={form.start_time || ""} onChange={(value) => set("start_time", value)} className="mt-1" /></div>
        <div><Label>Finish Time</Label><TimeScroller value={form.end_time || ""} onChange={(value) => set("end_time", value)} className="mt-1" /></div>
      </div>

      <div className="flex items-center justify-between rounded-xl border p-3">
        <div><Label>Rod not completed</Label><p className="text-xs text-slate-500">Show end stick up when the rod is not completed.</p></div>
        <Switch
          checked={!!form.rod_not_completed}
          onCheckedChange={(v) => {
            setForm((p) => ({
              ...p,
              rod_not_completed: v,
              end_stick_up: v ? (p.end_stick_up ?? undefined) : undefined,
            }));
          }}
        />
      </div>

      {form.rod_not_completed && (
        <div>
          <Label>End Stick Up (m)</Label>
          <Input type="number" step="0.01" value={form.end_stick_up ?? ""} onChange={(e) => set("end_stick_up", e.target.value === "" ? undefined : +e.target.value)} className="rounded-xl" />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div><Label>Lithology</Label><Input value={form.lithology || ""} onChange={(e) => set("lithology", e.target.value)} className="rounded-xl" /></div>
        <div className="hidden" aria-hidden="true" />
      </div>
      <div><Label>Remarks</Label><Textarea value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="rounded-xl min-h-[80px]" /></div>

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting} className="rounded-xl gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {initialData ? "Update Chip Section" : "Save Chip Section"}
        </Button>
      </div>
    </form>
  );
}
